import type { HTMLAttributes } from 'react';
import React, { forwardRef, useEffect, useRef } from 'react';
import { Slot } from '@radix-ui/react-slot';

const TOUCH_EVENT = { react: 'onTouchStart', native: 'touchstart' } as const;
const MOUSE_EVENT = { react: 'onMouseDown', native: 'mousedown' } as const;

const setUpReactEventHandlers = (
  handler: () => void,
  props: HTMLAttributes<unknown>,
): HTMLAttributes<unknown> => ({
  ...props,
  [TOUCH_EVENT.react]: (e) => {
    handler();
    props[TOUCH_EVENT.react]?.(e);
  },
  [MOUSE_EVENT.react]: (e) => {
    handler();
    props[MOUSE_EVENT.react]?.(e);
  },
});

interface ClickOutsideHandlerProps extends HTMLAttributes<HTMLDivElement> {
  onClickOutside?: ((e: Event) => unknown) | null;
  asChild?: boolean;
}

const ClickOutsideHandler = forwardRef<
  HTMLDivElement,
  ClickOutsideHandlerProps
>(({ onClickOutside, asChild, ...props }: ClickOutsideHandlerProps, ref) => {
  // To support React Portal.
  // Alternative to https://github.com/facebook/react/issues/14540
  const isTargetInsideReactTreeRef = useRef(false);

  const onClickOutsideRef = useRef(onClickOutside);
  useEffect(() => {
    onClickOutsideRef.current = onClickOutside;
  }, [onClickOutside]);

  useEffect(() => {
    const handler = (e: Event) => {
      if (!isTargetInsideReactTreeRef.current) {
        onClickOutsideRef.current?.(e);
      }

      isTargetInsideReactTreeRef.current = false;
    };

    document.addEventListener(TOUCH_EVENT.native, handler, { passive: true });
    document.addEventListener(MOUSE_EVENT.native, handler, { passive: true });
    return () => {
      document.removeEventListener(TOUCH_EVENT.native, handler);
      document.removeEventListener(MOUSE_EVENT.native, handler);
    };
  }, []);

  const handleReactEvent = () => {
    isTargetInsideReactTreeRef.current = true;
  };

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp ref={ref} {...setUpReactEventHandlers(handleReactEvent, props)} />
  );
});

export default ClickOutsideHandler;
